import { buildProductionPanelSelection } from './panel-selection';

export function createEmptySelectedObjects() {
  return {
    roofSegments: [],
    roofVertices: {},
    roofLines: {},
    obstacles: [],
    fireSetbacks: {},
    panels: {},
    trees: []
  };
}

export function isSelectionEmpty(objects) {
  if (!objects) return true;
  if (!Array.isArray(objects)) {
    const keys = Object.keys(objects);
    if (keys.length === 0) return true;
    return keys.every((key) => {
      const value = objects[key];
      return !Array.isArray(value) || value.length === 0;
    });
  }
  return objects.length === 0;
}

export function addToSelection(
  selectedObjects,
  index,
  objectType,
  { removeOld = false, currentAccordion = null } = {}
) {
  const objects = selectedObjects?.[objectType];
  let newObjects;
  if (!Array.isArray(objects)) {
    newObjects =
      !removeOld && objects ? JSON.parse(JSON.stringify(objects)) : {};
  } else {
    newObjects = !removeOld && objects ? [...objects] : [];
  }

  const items = Array.isArray(index) ? index : [index];
  const restrictPanelsToSingleRoof =
    objectType === 'panels' &&
    (currentAccordion === 'design' || currentAccordion === 'revise');
  const targetPanelRoof = restrictPanelsToSingleRoof
    ? items
        .map((item) =>
          typeof item !== 'number' ? String(item).split('-')[0] : null
        )
        .find((value) => value !== null)
    : null;

  if (targetPanelRoof !== null) {
    Object.keys(newObjects).forEach((panelIndex) => {
      if (panelIndex !== targetPanelRoof) delete newObjects[panelIndex];
    });
  }

  items.forEach((item) => {
    if (typeof item !== 'number') {
      const [panelIndex, vertexIndex] = String(item).split('-');
      if (targetPanelRoof !== null && panelIndex !== targetPanelRoof) return;
      const numericVertex = parseInt(vertexIndex, 10);
      if (
        !(panelIndex in newObjects) ||
        !Array.isArray(newObjects[panelIndex])
      ) {
        newObjects[panelIndex] = [];
      }
      if (!newObjects[panelIndex].includes(numericVertex)) {
        newObjects[panelIndex].push(numericVertex);
      }
    } else if (!newObjects.includes(item)) {
      newObjects.push(item);
    }
  });

  Object.keys(newObjects)
    .reverse()
    .forEach((key) => {
      if (newObjects[key] === undefined) delete newObjects[key];
    });

  return {
    ...selectedObjects,
    [objectType]: newObjects
  };
}

export function removeFromSelection(selectedObjects, index, objectType) {
  const objects = selectedObjects?.[objectType];
  let newObjects;
  if (!Array.isArray(objects)) {
    newObjects = objects ? JSON.parse(JSON.stringify(objects)) : {};
  } else {
    newObjects = objects ? [...objects] : [];
  }

  const items = Array.isArray(index) ? index : [index];
  items.forEach((item) => {
    if (typeof item !== 'number') {
      const [panelIndex, vertexIndex] = String(item).split('-');
      const numericVertex = parseInt(vertexIndex, 10);
      if (!(panelIndex in newObjects)) return;
      if (newObjects[panelIndex].includes(numericVertex)) {
        newObjects[panelIndex].splice(
          newObjects[panelIndex].indexOf(numericVertex),
          1
        );
      }
    } else if (newObjects.includes(item)) {
      newObjects.splice(newObjects.indexOf(item), 1);
    }
  });

  Object.keys(newObjects)
    .reverse()
    .forEach((key) => {
      if (newObjects[key] === undefined) delete newObjects[key];
    });

  return {
    ...selectedObjects,
    [objectType]: newObjects
  };
}

export function buildSelectAllObjects(
  objectType,
  { segments = [], panels = [] } = {}
) {
  switch (objectType) {
    case 'roofSegments':
      return segments.map((_, segmentIndex) => segmentIndex);
    case 'roofVertices':
    case 'fireSetbacks': {
      const selected = {};
      segments.forEach((segment, segmentIndex) => {
        selected[segmentIndex] = Array.isArray(segment?.geometry)
          ? segment.geometry.map((_, vertexIndex) => vertexIndex)
          : [];
      });
      return selected;
    }
    case 'panels': {
      const selected = {};
      panels.forEach((panel, panelIndex) => {
        const allPanels = [
          ...(panel?.vertical?.points || []),
          ...(panel?.horizontal?.points || [])
        ];
        selected[panelIndex] = allPanels.map((_, index) => index);
      });
      return selected;
    }
    default:
      return Array.isArray(selectedObjectsDefault(objectType)) ? [] : {};
  }
}

function selectedObjectsDefault(objectType) {
  const empty = createEmptySelectedObjects();
  return empty[objectType];
}

export function clearSelection(
  selectedObjects,
  objectType,
  { forceClear = false, segments = [], panels = [] } = {}
) {
  const objects = selectedObjects?.[objectType];
  const empty = isSelectionEmpty(objects);

  if (!empty || forceClear) {
    const current = selectedObjects || createEmptySelectedObjects();
    return {
      ...current,
      [objectType]: selectedObjectsDefault(objectType)
    };
  }

  return {
    ...selectedObjects,
    [objectType]: buildSelectAllObjects(objectType, { segments, panels })
  };
}

export function syncSelectedRoofFrom3D(state) {
  const selectedSegments = state?.selectedObjects?.roofSegments || [];
  const selectedRoof = selectedSegments.length
    ? selectedSegments[selectedSegments.length - 1]
    : -1;
  if (state?.selectedRoof === selectedRoof) return null;
  return { selectedRoof };
}

export function syncSelectedRoofFrom2D(state) {
  const roofSegments =
    state?.selectedRoof !== undefined && state?.selectedRoof !== -1
      ? [state.selectedRoof]
      : [];
  const current = state?.selectedObjects?.roofSegments || [];
  const unchanged =
    current.length === roofSegments.length &&
    current.every((value, index) => value === roofSegments[index]);
  if (unchanged) return null;
  return {
    selectedObjects: {
      ...state.selectedObjects,
      roofSegments
    }
  };
}

export function syncSelectedPanelFrom3D(state) {
  const selectedPanels = state?.selectedObjects?.panels || {};
  const result = buildProductionPanelSelection(
    state?.panels || [],
    selectedPanels
  );
  return {
    panels: result.panels,
    selectedPanels: result.hasSelectedPanels
  };
}

export function syncSelectedPanelFrom2D(state) {
  const panels = Array.isArray(state?.panels) ? state.panels : [];
  const selectedPanels = {};
  panels.forEach((panel, segmentIndex) => {
    const selectedList = [];
    (panel?.horizontal?.selected || []).forEach((selected, panelIndex) => {
      if (selected) selectedList.push(panelIndex);
    });
    const horizontalCount = Array.isArray(panel?.horizontal?.points)
      ? panel.horizontal.points.length
      : 0;
    (panel?.vertical?.selected || []).forEach((selected, panelIndex) => {
      if (selected) selectedList.push(panelIndex + horizontalCount);
    });
    selectedPanels[segmentIndex] = selectedList;
  });

  return {
    selectedObjects: {
      ...(state?.selectedObjects || createEmptySelectedObjects()),
      panels: selectedPanels
    }
  };
}

export function clearPanelSelectionFlags(state) {
  const panels = Array.isArray(state?.panels)
    ? state.panels.map((panel) => {
        const horizontalPoints = panel?.horizontal?.points || [];
        const verticalPoints = panel?.vertical?.points || [];
        return {
          ...panel,
          horizontal: {
            ...panel.horizontal,
            selected: Array(horizontalPoints.length).fill(false)
          },
          vertical: {
            ...panel.vertical,
            selected: Array(verticalPoints.length).fill(false)
          }
        };
      })
    : state?.panels;
  return {
    ...state,
    panels,
    selectedPanels: false,
    selectedObjects: {
      ...(state?.selectedObjects || createEmptySelectedObjects()),
      panels: {}
    }
  };
}

export function applyAddToSelection(state, index, objectType, options = {}) {
  const selectedObjects = addToSelection(
    state.selectedObjects || createEmptySelectedObjects(),
    index,
    objectType,
    {
      removeOld: options.removeOld,
      currentAccordion: options.currentAccordion ?? state.currentAccordion
    }
  );
  let next = { ...state, selectedObjects };
  if (objectType === 'panels') {
    next = { ...next, ...syncSelectedPanelFrom3D(next) };
  } else if (objectType === 'roofSegments') {
    const roofPatch = syncSelectedRoofFrom3D(next);
    if (roofPatch) next = { ...next, ...roofPatch };
  }
  return next;
}

export function applyRemoveFromSelection(state, index, objectType) {
  const selectedObjects = removeFromSelection(
    state.selectedObjects || createEmptySelectedObjects(),
    index,
    objectType
  );
  let next = { ...state, selectedObjects };
  if (objectType === 'panels') {
    next = { ...next, ...syncSelectedPanelFrom3D(next) };
  } else if (objectType === 'roofSegments') {
    const roofPatch = syncSelectedRoofFrom3D(next);
    if (roofPatch) next = { ...next, ...roofPatch };
  }
  return next;
}

export function applyClearSelection(state, objectType, forceClear = false) {
  const selectedObjects = clearSelection(
    state.selectedObjects || createEmptySelectedObjects(),
    objectType,
    {
      forceClear,
      segments: state.segments || [],
      panels: state.panels || []
    }
  );
  let next = { ...state, selectedObjects };
  if (objectType === 'panels') {
    next = { ...next, ...syncSelectedPanelFrom3D(next) };
  } else if (objectType === 'roofSegments') {
    const roofPatch = syncSelectedRoofFrom3D(next);
    if (roofPatch) next = { ...next, ...roofPatch };
  }
  return next;
}
